import React, { useState, useEffect, useMemo } from 'react';
import { Box, Typography, Button, TextField, MenuItem, IconButton, CircularProgress, Alert } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { ALL_BANKS } from '../../../constants/banks';

const ACCOUNT_TYPES = [
  { id: 'own', label: 'Own Account' },
  { id: 'joint', label: 'Joint Account' },
];

const CURRENCIES = ['EUR', 'CHF', 'USD', 'GBP', 'JPY'];

export default function Settings() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({ bank_id: '', currency: '', account_type: '' });
  const [submitting, setSubmitting] = useState(false);

  // Fetch accounts on mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  const fetchAccounts = async () => {
    try {
      setLoading(true);
      const response = await fetch('/api/accounts');
      if (!response.ok) throw new Error('Failed to fetch accounts');
      const data = await response.json();
      setAccounts(data);
      setError(null);
    } catch (err) {
      console.error('Error fetching accounts:', err);
      setError('Failed to load accounts');
    } finally {
      setLoading(false);
    }
  };

  const handleAddAccount = async (e) => {
    e.preventDefault();
    if (!formData.bank_id || !formData.currency || !formData.account_type) {
      setError('Please fill in all fields');
      return;
    }

    try {
      setSubmitting(true);
      const response = await fetch('/api/accounts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(formData),
      });
      if (!response.ok) throw new Error('Failed to add account');
      const newAccount = await response.json();
      setAccounts([newAccount, ...accounts]);
      setFormData({ bank_id: '', currency: '', account_type: '' });
      setError(null);
    } catch (err) {
      console.error('Error adding account:', err);
      setError('Failed to add account');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteAccount = async (id) => {
    try {
      const response = await fetch(`/api/accounts/${id}`, { method: 'DELETE' });
      if (!response.ok) throw new Error('Failed to delete account');
      setAccounts(accounts.filter((acc) => acc.id !== id));
      setError(null);
    } catch (err) {
      console.error('Error deleting account:', err);
      setError('Failed to delete account');
    }
  };

  const bankLabelMap = useMemo(() => {
    const map = {};
    ALL_BANKS.forEach((b) => {
      map[b.id] = b.label;
    });
    return map;
  }, []);

  const accountTypeMap = useMemo(() => {
    const map = {};
    ACCOUNT_TYPES.forEach((t) => {
      map[t.id] = t.label;
    });
    return map;
  }, []);

  const panelStyle = {
    border: '1px solid #D4B896',
    borderRadius: '8px',
    p: 3,
    bgcolor: '#FBF5EC',
    width: '100%',
    maxWidth: 600,
  };

  const formStyle = {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    mb: 3,
  };

  const accountRowStyle = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    p: 2,
    mb: 1,
    bgcolor: '#FFFFFF',
    border: '1px solid #E8D9C3',
    borderRadius: '6px',
    '&:hover': { bgcolor: '#F9F2E8' },
  };

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      pt={4}
      px={4}
      minWidth={300}
    >
      <Typography
        variant="h6"
        sx={{ color: '#2B1810', fontFamily: 'Courier New', mb: 4, letterSpacing: 2 }}
      >
        Account Management
      </Typography>

      {error && <Alert severity="error" sx={{ mb: 2, width: '100%', maxWidth: 600 }}>{error}</Alert>}

      {/* Add Account Form */}
      <Box sx={panelStyle}>
        <Typography
          sx={{ color: '#7A5035', fontFamily: 'Courier New', fontSize: '0.75rem', letterSpacing: 2, mb: 2 }}
        >
          ADD NEW ACCOUNT
        </Typography>
        <Box component="form" onSubmit={handleAddAccount} sx={formStyle}>
          <TextField
            select
            label="Bank"
            value={formData.bank_id}
            onChange={(e) => setFormData({ ...formData, bank_id: e.target.value })}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF' },
              '& .MuiInputLabel-root': { color: '#7A5035' },
            }}
          >
            {ALL_BANKS.map((bank) => (
              <MenuItem key={bank.id} value={bank.id}>
                {bank.label}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Currency"
            value={formData.currency}
            onChange={(e) => setFormData({ ...formData, currency: e.target.value })}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF' },
              '& .MuiInputLabel-root': { color: '#7A5035' },
            }}
          >
            {CURRENCIES.map((curr) => (
              <MenuItem key={curr} value={curr}>
                {curr}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            select
            label="Account Type"
            value={formData.account_type}
            onChange={(e) => setFormData({ ...formData, account_type: e.target.value })}
            fullWidth
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': { backgroundColor: '#FFFFFF' },
              '& .MuiInputLabel-root': { color: '#7A5035' },
            }}
          >
            {ACCOUNT_TYPES.map((type) => (
              <MenuItem key={type.id} value={type.id}>
                {type.label}
              </MenuItem>
            ))}
          </TextField>

          <Button
            type="submit"
            variant="contained"
            disabled={submitting || !formData.bank_id || !formData.currency || !formData.account_type}
            sx={{
              bgcolor: '#D4B896',
              color: '#2B1810',
              fontFamily: 'Courier New',
              fontWeight: 600,
              '&:hover': { bgcolor: '#C4A87C' },
              '&:disabled': { bgcolor: '#E8D9C3', color: '#9A7055' },
            }}
            startIcon={<AddIcon />}
          >
            {submitting ? 'Adding...' : 'Add Account'}
          </Button>
        </Box>
      </Box>

      {/* Accounts List */}
      <Box sx={{ ...panelStyle, mt: 4 }}>
        <Typography
          sx={{ color: '#7A5035', fontFamily: 'Courier New', fontSize: '0.75rem', letterSpacing: 2, mb: 2 }}
        >
          YOUR ACCOUNTS ({accounts.length})
        </Typography>

        {loading ? (
          <Box display="flex" justifyContent="center" py={3}>
            <CircularProgress size={40} sx={{ color: '#D4B896' }} />
          </Box>
        ) : accounts.length === 0 ? (
          <Typography sx={{ color: '#9A7055', fontFamily: 'Courier New', py: 2 }}>
            No accounts yet. Add your first account above.
          </Typography>
        ) : (
          accounts
            .sort((a, b) => {
              const aLabel = `${bankLabelMap[a.bank_id] || a.bank_id} - ${a.currency}`;
              const bLabel = `${bankLabelMap[b.bank_id] || b.bank_id} - ${b.currency}`;
              return aLabel.localeCompare(bLabel);
            })
            .map((account) => (
            <Box key={account.id} sx={accountRowStyle}>
              <Box>
                <Typography sx={{ fontFamily: 'Courier New', fontWeight: 600, color: '#2B1810' }}>
                  {bankLabelMap[account.bank_id]} ({account.currency})
                </Typography>
                <Typography sx={{ fontFamily: 'Courier New', fontSize: '0.85rem', color: '#7A5035' }}>
                  {accountTypeMap[account.account_type]}
                </Typography>
              </Box>
              <IconButton
                size="small"
                onClick={() => handleDeleteAccount(account.id)}
                sx={{ color: '#9A7055', '&:hover': { color: '#D4534F' } }}
              >
                <DeleteIcon fontSize="small" />
              </IconButton>
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
}

Settings.propTypes = {};
